import log from '@/lib/logger';
import {Dialog, DialogActions, DialogTitle} from "@/components/tailwind/dialog.tsx";
import {useState} from "react";
import {Button} from "@/components/tailwind/button.tsx";
import {Input} from "@/components/tailwind/input.tsx";
import {UserChangePasswordDTO, UserProfile} from "@/models/user.ts";
import {useAuth} from "@/context/use-auth.tsx";
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {z} from "zod";
import {Subheading} from "@/components/tailwind/heading.tsx";
import {Text} from "@/components/tailwind/text.tsx";
import ValidationAlert from "@/components/validation-alert.tsx";
import PasswordStrengthIndicator from "@/components/password-strength-indicator.tsx";
import {useTranslation} from "react-i18next";
import {changeMyPassword, changePassword} from "@/services/user-services.ts";
import {useToast} from "@/hooks/use-toast.ts";
import {ProblemDetail} from "@/types/problem-details.ts";
import {usePasswordPolicy} from "@/hooks/use-password-policy.ts";
import {
    collectPasswordPolicyViolations,
    type PasswordPolicyViolation,
} from "@/lib/password-policy-violations.ts";

const POLICY_VIOLATION_KEYS: Record<PasswordPolicyViolation['kind'], string> = {
    'min-length': 'changePasswordDialog.errors.password-min-length',
    'requires-digit': 'changePasswordDialog.errors.password-requires-digit',
    'requires-special-char': 'changePasswordDialog.errors.password-requires-special-char',
    'min-distinct-chars': 'changePasswordDialog.errors.password-min-distinct-chars',
}

interface ChangePasswordDialogProps {
    effectedUserId: string;
    standalone?: boolean;
    onClose?: () => void;
    isInitiallyOpen?: boolean;
    isOwnPassword?: boolean;
    // Names that zxcvbn-ts should treat as forbidden substrings (username, real name, etc.). When
    // omitted the dialog falls back to the authenticated user's username for the own-password flow.
    userInputs?: string[];
}

type ChangePasswordFormData = {
    id?: string,
    currentPassword: string | undefined,
    newPassword: string,
    confirmNewPassword: string,
}

function createSchema(isCurrentUser: boolean) {
    return z.object({
        currentPassword: isCurrentUser
            ? z.string().min(1, "changePasswordDialog.errors.missing-current-password")
            : z.string().optional(),
        // Only presence is checked client-side; the configurable backend policy owns the minimum
        // length and surfaces it as a finding whose interpolation args carry the resolved value.
        newPassword:
            z.string({error: "changePasswordDialog.errors.missing-new-password"})
                .min(1, "changePasswordDialog.errors.missing-new-password"),
        confirmNewPassword:
            z.string({error: "changePasswordDialog.errors.missing-confirm-password"})
    }).refine((data) => data.newPassword === data.confirmNewPassword, {
        message: "changePasswordDialog.errors.password-mismatch",
        path: ["confirmNewPassword"],
    });
}

export default function ChangePasswordDialog({
                                                 effectedUserId,
                                                 standalone = false,
                                                 onClose,
                                                 isInitiallyOpen = false,
                                                 isOwnPassword = false,
                                                 userInputs,
                                             }: ChangePasswordDialogProps) {
    const [isOpen, setIsOpen] = useState(isInitiallyOpen)
    const {user, token} = useAuth()
    const {toast} = useToast()
    const {t} = useTranslation(['login', 'common']) as any

    // useForm must be called unconditionally (React hooks rules)
    // We default to requiring current password if user is not yet available
    const changeOwnPassword = user ? user.userId === effectedUserId : false

    const {
        register,
        handleSubmit,
        reset,
        setError,
        formState: {errors},
        watch,
    } = useForm<ChangePasswordFormData>({resolver: zodResolver(createSchema(changeOwnPassword))});

    const newPasswordValue = watch("newPassword") ?? ""
    const strengthUserInputs = userInputs ?? (changeOwnPassword && user?.userName ? [user.userName] : [])
    const passwordPolicy = usePasswordPolicy(token)

    if (!user) {
        return null;
    }
    
    // Check authorization
    if (neitherUserNorAdmin(user, effectedUserId)) {
        return null;
    }

    function showSuccessToast() {
        toast({
            title: t("common:toast.success.title"),
            description: t("common:toast.success.description"),
            variant: "default",
        })
    }

    function handleClose() {
        setIsOpen(false)
        reset({
            currentPassword: '',
            newPassword: '',
            confirmNewPassword: ''
        })
        onClose?.()
    }

    async function onSubmit(data: ChangePasswordFormData) {

        const violations = collectPasswordPolicyViolations(data.newPassword, passwordPolicy)
        if (violations.length > 0) {
            const first = violations[0]
            setError('newPassword', {
                message: t(POLICY_VIOLATION_KEYS[first.kind], {
                    minLength: first.minLength,
                    minDistinctChars: first.minDistinctChars,
                }),
            })
            return
        }

        try {
            const changePasswordRequest = {
                oldPassword: data.currentPassword,
                newPassword: data.newPassword
            } as UserChangePasswordDTO

            let changePasswordResponse;

            if (isOwnPassword) {
                changePasswordResponse = changeMyPassword(changePasswordRequest, token!);
            } else {
                changePasswordResponse = changePassword(changePasswordRequest, effectedUserId, token!);
            }

            changePasswordResponse
                .then((_) => {
                    handleClose()
                    showSuccessToast()
                })
                .catch((reason: ProblemDetail) => {
                    log.warn("An error occurred while changing the password", reason)

                    if (reason.validationIssues) {
                        reason.validationIssues.map(
                            finding => {
                                setError(
                                    finding.field as keyof Omit<ChangePasswordFormData, 'id'>,
                                    {
                                        // Resolve the key with the finding's own interpolation args (e.g. the
                                        // backend-resolved {{minLength}}) so the message reflects the effective
                                        // policy rather than a client-side fallback. Mirrors UserForm.applyServerError.
                                        message: t(finding.message, finding.args)
                                    }
                                )
                            }
                        )
                    } else {

                        if (reason.status) {
                            setError(
                                "root.serverError",
                                {
                                    type: `${reason.status}`,
                                    message: t("common:serverError.responseCode", {statusCode: reason.status})
                                }
                            )
                        } else {
                            setError(
                                "root.serverError",
                                {
                                    type: `serverError`,
                                    message: t("common:serverError.unknown")
                                }
                            )
                        }
                    }
                })

        } catch (error) {
            log.error("Error while changing password:", error);

            setError(
                "root.serverError",
                {
                    type: `serverError`,
                    message: t("common:serverError.unknownWithDetails", {errorDetails: error})
                }
            )
        }
    }

    return (
        <>

            {!standalone && (
                <Button type="button"
                        onClick={() => setIsOpen(true)}
                        aria-label={t("changePasswordDialog.buttons.openDialog.ariaLabel")}
                        data-testid="open-change-password-dialog"
                >
                    {t("changePasswordDialog.buttons.openDialog.text")}
                </Button>
            )}

            <Dialog
                open={isOpen}
                onClose={handleClose}
            >
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    <DialogTitle>{t("changePasswordDialog.title")}</DialogTitle>

                    {errors.root?.serverError && (
                        <ValidationAlert message={errors.root.serverError.message || t("serverError.unknown")}/>
                    )}
                    {errors.id && (
                        <ValidationAlert message={t(errors.id.message || "changePasswordDialog.errors.invalid-id")}/>
                    )}

                    <div className="space-y-6">
                        {changeOwnPassword && (
                            <div className="grid gap-y-6 sm:grid-cols-2">
                                <div className="space-y-1">
                                    <Subheading id="currentPasswordHeading">
                                        {t("changePasswordDialog.fields.currentPassword.heading")}
                                    </Subheading>
                                    <Text>
                                        {t("changePasswordDialog.fields.currentPassword.description")}
                                    </Text>
                                </div>
                                <div>
                                    <Input
                                        type="password"
                                        id="current-password"
                                        {...register("currentPassword")}
                                        placeholder={t("changePasswordDialog.fields.currentPassword.placeholder")}
                                        autoComplete="current-password"
                                        data-invalid={errors.currentPassword || null}
                                        data-testid="change-password-current"
                                    />
                                    {errors.currentPassword && (
                                        <ValidationAlert message={t(errors.currentPassword.message ||
                                            "changePasswordDialog.errors.unknown-current-password-issue")}
                                        />
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="grid gap-y-6 sm:grid-cols-2">
                            <div className="space-y-1">
                                <Subheading id="newPasswordHeading">
                                    {t("changePasswordDialog.fields.newPassword.heading")}
                                </Subheading>
                                <Text>
                                    {t("changePasswordDialog.fields.newPassword.description")}
                                </Text>
                            </div>
                            <div className="space-y-4">
                                <div>
                                    <Input
                                        type="password"
                                        {...register("newPassword")}
                                        placeholder={t("changePasswordDialog.fields.newPassword.placeholder")}
                                        autoComplete="new-password"
                                        data-invalid={errors.newPassword || null}
                                        data-testid="change-password-new"
                                    />
                                    <PasswordStrengthIndicator
                                        password={newPasswordValue}
                                        userInputs={strengthUserInputs}
                                        minScore={passwordPolicy.minStrengthScore}
                                    />
                                    {errors.newPassword && (
                                        <ValidationAlert message={t(errors.newPassword.message ||
                                            "changePasswordDialog.errors.unknown-new-password-issue")}
                                        />
                                    )}
                                </div>
                                <div>
                                    <Input
                                        type="password"
                                        {...register("confirmNewPassword")}
                                        placeholder={t("changePasswordDialog.fields.confirmPassword.placeholder")}
                                        autoComplete="new-password"
                                        data-invalid={errors.confirmNewPassword || null}
                                        data-testid="change-password-confirm"
                                    />
                                    {errors.confirmNewPassword && (
                                        <ValidationAlert message={t(errors.confirmNewPassword.message ||
                                            "changePasswordDialog.errors.unknown-confirm-password-issue")}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <DialogActions>
                        <Button
                            type="button"
                            plain
                            onClick={handleClose}
                            aria-label={t("common:buttons.cancel.ariaLabel")}
                        >
                            {t("common:buttons.cancel.text")}
                        </Button>
                        <Button
                            type="submit"
                            aria-label={t("changePasswordDialog.buttons.submit.ariaLabel")}
                            data-testid="change-password-submit"
                        >
                            {t("changePasswordDialog.buttons.submit.text")}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </>
    )
}

function neitherUserNorAdmin(user: UserProfile, effectedUserId: string) {
    return user.userId !== effectedUserId && user.role !== "ADMIN"
}
